import styled, { css } from 'styled-components'

export const SheetOverlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: ${({ theme }) => theme.zIndex.modal};
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.3s ease;

  ${({ $isOpen }) =>
    $isOpen &&
    css`
      opacity: 1;
      pointer-events: auto;
    `}
`

export const SheetContainer = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${({ theme }) => theme.colors.background.primary};
  z-index: ${({ theme }) => theme.zIndex.modal + 1};
  display: flex;
  flex-direction: column;
  transform: translateY(100%);
  transition: transform 0.35s cubic-bezier(0.4, 0, 0.2, 1);

  ${({ $isOpen }) =>
    $isOpen &&
    css`
      transform: translateY(0);
    `}
`

export const SheetHeader = styled.div`
  position: sticky;
  top: 0;
  background: ${({ theme }) => theme.colors.background.primary};
  z-index: ${({ theme }) => theme.zIndex.elevated};
  display: flex;
  flex-direction: row;
  align-items: center;
  min-height: 56px;
  padding: 0 ${({ theme }) => theme.spacing.medium};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};
`

export const SheetBackButton = styled.button`
  background: none;
  border: none;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xsmall};
  color: ${({ theme }) => theme.colors.primary};
  cursor: pointer;
  touch-action: manipulation;
  min-width: ${({ theme }) => theme.touchTargets.minimum};
  min-height: ${({ theme }) => theme.touchTargets.minimum};
  padding: 0;
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};

  &:focus {
    outline: none;
  }
`

export const SheetTitle = styled.div`
  flex: 1;
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding: 0 ${({ theme }) => theme.spacing.small};
`

export const SheetHeaderActions = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${({ theme }) => theme.spacing.small};
  align-items: center;
`

export const SheetContent = styled.div`
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding-bottom: env(safe-area-inset-bottom, 0px);
`

export const SheetFooter = styled.div`
  position: sticky;
  bottom: 0;
  background: ${({ theme }) => theme.colors.background.primary};
  z-index: ${({ theme }) => theme.zIndex.elevated};
  border-top: 1px solid ${({ theme }) => theme.colors.border.light};
  padding: ${({ theme }) => theme.spacing.medium};
  padding-bottom: calc(
    ${({ theme }) => theme.spacing.medium} + env(safe-area-inset-bottom, 0px)
  );
`

export const SheetStickyTabs = styled.div`
  position: sticky;
  top: 56px;
  background: ${({ theme }) => theme.colors.background.primary};
  z-index: ${({ theme }) => theme.zIndex.elevated};
  display: flex;
  flex-direction: row;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};
`

export const SheetTab = styled.button<{ $active: boolean }>`
  flex: 1;
  padding: ${({ theme }) => theme.spacing.small} 0;
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  border: none;
  background: transparent;
  cursor: pointer;
  touch-action: manipulation;
  transition: ${({ theme }) => theme.transitions.fast};

  ${({ $active, theme }) =>
    $active
      ? css`
          color: ${theme.colors.primary};
          border-bottom: 3px solid ${theme.colors.primary};
        `
      : css`
          color: ${theme.colors.text.tertiary};
          border-bottom: 3px solid transparent;
        `}

  &:focus {
    outline: none;
  }
`

// --- Shared Detail Building Blocks ---

export const DetailSectionLabel = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.micro};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.tertiary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: ${({ theme }) => `0 ${theme.spacing.medium}`};
  margin-top: ${({ theme }) => theme.spacing.medium};
`

export const DetailCard = styled.div`
  background: ${({ theme }) => theme.colors.background.primary};
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  margin: ${({ theme }) => `${theme.spacing.small} ${theme.spacing.medium}`};
  overflow: hidden;
`

export const DetailInfoGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0;
`

export const DetailInfoItem = styled.div`
  padding: ${({ theme }) => `${theme.spacing.small} ${theme.spacing.medium}`};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};
  &:nth-last-child(-n + 2) {
    border-bottom: none;
  }
`

export const DetailInfoLabel = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.micro};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.tertiary};
  text-transform: uppercase;
`

export const DetailInfoValue = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-top: 2px;
`

export const DetailAvatar = styled.div<{
  $color: string
  $bg: string
}>`
  width: 28px;
  height: 28px;
  border-radius: ${({ theme }) => theme.borderRadius.circle};
  background: ${({ $bg }) => $bg};
  color: ${({ $color }) => $color};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 9px;
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  flex-shrink: 0;
`

export const DetailParticipantRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.small};
  padding: ${({ theme }) => `${theme.spacing.xsmall} ${theme.spacing.medium}`};
`

export const DetailParticipantInfo = styled.div`
  flex: 1;
  min-width: 0;
`

export const DetailParticipantName = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.primary};
`

export const DetailParticipantType = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  color: ${({ theme }) => theme.colors.text.tertiary};
`

export const DetailListItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.small};
  padding: ${({ theme }) => `${theme.spacing.small} ${theme.spacing.medium}`};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};
  &:last-child {
    border-bottom: none;
  }
`

export const DetailListNumber = styled.div<{
  $color?: string
}>`
  width: 24px;
  height: 24px;
  border-radius: ${({ theme }) => theme.borderRadius.circle};
  background: ${({ $color, theme }) =>
    $color ? `${$color}15` : `${theme.colors.primary}15`};
  color: ${({ $color, theme }) => $color || theme.colors.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${({ theme }) => theme.typography.fontSize.micro};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  flex-shrink: 0;
`

export const DetailListInfo = styled.div`
  flex: 1;
  min-width: 0;
`

export const DetailListMain = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.primary};
`

export const DetailListSub = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  color: ${({ theme }) => theme.colors.text.tertiary};
`

export const DetailViewLink = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  cursor: pointer;
  white-space: nowrap;
  padding: 0;
`

export const DetailValueCard = styled.div<{
  $bg?: string
}>`
  background: ${({ $bg, theme }) => $bg || theme.colors.background.secondary};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  margin: ${({ theme }) => `${theme.spacing.small} ${theme.spacing.medium}`};
  padding: ${({ theme }) => theme.spacing.medium};
`

export const DetailValueRow = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.medium};
`

export const DetailValueBlock = styled.div<{
  $flex?: number
}>`
  flex: ${({ $flex }) => $flex ?? 1};
  min-width: 0;
`

export const DetailValueLabel = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.micro};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.tertiary};
  text-transform: uppercase;
`

export const DetailValueAmount = styled.div<{
  $large?: boolean
}>`
  font-size: ${({ $large, theme }) =>
    $large
      ? theme.typography.fontSize.title
      : theme.typography.fontSize.section};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
`

export const DetailQuickStats = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.small};
  padding: ${({ theme }) => `${theme.spacing.small} ${theme.spacing.medium}`};
`

export const DetailStatItem = styled.div`
  flex: 1;
  background: ${({ theme }) => theme.colors.background.secondary};
  border-radius: ${({ theme }) => theme.borderRadius.default};
  padding: ${({ theme }) => theme.spacing.small};
`

export const DetailStatValue = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.section};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
`

export const DetailStatLabel = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.micro};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.tertiary};
  text-transform: uppercase;
`

export const DetailActionRow = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.small};
  padding: ${({ theme }) => `${theme.spacing.small} ${theme.spacing.medium}`};
`

export const DetailActionPill = styled.button<{
  $active?: boolean
}>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xsmall};
  padding: ${({ theme }) => `${theme.spacing.xsmall} ${theme.spacing.small}`};
  border-radius: ${({ theme }) => theme.borderRadius.large};
  border: none;
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  cursor: pointer;
  background: ${({ $active, theme }) =>
    $active ? theme.colors.primaryLight : theme.colors.background.secondary};
  color: ${({ $active, theme }) =>
    $active ? theme.colors.primary : theme.colors.text.secondary};
`

export const DetailBadge = styled.span<{
  $bg: string
  $color: string
}>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: ${({ theme }) => theme.borderRadius.small};
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  background: ${({ $bg }) => $bg};
  color: ${({ $color }) => $color};
`

export const DetailLoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.huge};
  color: ${({ theme }) => theme.colors.text.tertiary};
`

export const DetailFooterButtons = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.small};
`

export const DetailBadgeRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.small};
  padding: ${({ theme }) => `${theme.spacing.small} ${theme.spacing.medium}`};
  flex-wrap: wrap;
`

export const DetailAddress = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.section};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  padding: ${({ theme }) => `0 ${theme.spacing.medium}`};
`

export const DetailSubtitle = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  color: ${({ theme }) => theme.colors.text.tertiary};
  padding: ${({ theme }) => `2px ${theme.spacing.medium} 0`};
`

export const DetailMapPlaceholder = styled.div`
  height: 120px;
  margin: ${({ theme }) => `${theme.spacing.small} ${theme.spacing.medium}`};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  background: ${({ theme }) => theme.colors.background.tertiary};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.text.tertiary};
  font-size: ${({ theme }) => theme.typography.fontSize.small};
`

export const DetailPlaceholder = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing.huge};
  color: ${({ theme }) => theme.colors.text.tertiary};
  font-size: ${({ theme }) => theme.typography.fontSize.body};
`

export const DetailGroupTitle = styled.div<{
  $color?: string
}>`
  font-size: ${({ theme }) => theme.typography.fontSize.micro};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ $color, theme }) => $color || theme.colors.text.tertiary};
  text-transform: uppercase;
  letter-spacing: 0.3px;
  padding: ${({ theme }) => `${theme.spacing.small} ${theme.spacing.medium} 0`};
`

export const DetailDivider = styled.div`
  height: 1px;
  background: ${({ theme }) => theme.colors.border.light};
  margin: 0 ${({ theme }) => theme.spacing.medium};
`
